<script setup lang="ts">
import type { NavProductGroup } from "~~/shared/types/navigation";

/**
 * Middle column(s) of the mega menu - "By Product"
 * Displays a group of services with icons and descriptions
 * Following pattern for scannable product listings
 */

defineProps<{
  group: NavProductGroup;
}>();

const emit = defineEmits<{
  linkClick: [];
}>();
</script>

<template>
  <div class="space-y-5">
    <!-- Section title (if provided) -->
    <h4 v-if="group.title" class="text-sm font-semibold text-neutral-400">
      {{ group.title }}
    </h4>

    <!-- Product items -->
    <ul class="space-y-8">
      <li v-for="item in group.items" :key="item.href">
        <NuxtLink
          :to="item.href"
          :target="item.isExternal ? '_blank' : undefined"
          :rel="item.isExternal ? 'noopener noreferrer' : undefined"
          class="group flex items-start gap-3"
          @click="emit('linkClick')"
        >
          <!-- Icon -->
          <Icon
            v-if="item.icon"
            :name="item.icon"
            size="20"
            class="mt-0.5 shrink-0 text-primary-500 transition-transform duration-200 group-hover:scale-110"
            aria-hidden="true"
          />

          <div class="min-w-0">
            <!-- Label (bold, italic) -->
            <span
              class="block text-sm font-bold text-slate-800 transition-colors group-hover:text-primary-500"
            >
              {{ item.label }}
            </span>

            <!-- Description -->
            <span
              v-if="item.description"
              class="mt-0.5 block text-sm leading-snug text-neutral-500"
            >
              {{ item.description }}
            </span>
          </div>
        </NuxtLink>
      </li>
    </ul>
  </div>
</template>
